import { environment } from 'src/environments/environment';
import { roles } from '../enumerations/constants';

// Which dashboard a user can use (logistics = the main dashboard, late orders and widgets; warehouse),
// and which one they land on.
export type DashboardArea = 'logistics' | 'warehouse';

export const AREA_PATHS: Record<DashboardArea, string> = {
  logistics: '/dashboard',
  warehouse: '/warehouse'
};

const LOGISTICS_ROLE_IDS: number[] = [roles.Supervisor, roles.SuperUser, roles.FleetSupervisor, roles.CustomerSupport];

// The driver dashboard (/driver) is not a landing area: the header only links to it. Its API decides access from the
// setting driver_dashboard_roles (4, 5, 11, 12 on 4 Nov 2026) and answers 403 to other roles, which shows "No access".
export const DRIVER_DASHBOARD_PATH = '/driver';
const DRIVER_ROLE_IDS: number[] = [roles.Supervisor, roles.SuperUser, roles.FleetSupervisor, roles.CustomerSupport];

// Matched by title because the ids of these roles may change. Highest first (used for the header label).
const WAREHOUSE_ROLE_TITLES = ['Warehouse Super User', 'Warehouse Admin'];

const ONE_YEAR_SEC = 365 * 24 * 60 * 60;

export function storedUser(): any {
  try {
    return JSON.parse(localStorage.getItem(environment.storage.userData) ?? 'null');
  } catch {
    return null;
  }
}

export function canUseArea(user: any, area: DashboardArea): boolean {
  return area === 'logistics'
    ? userRoles(user).some(r => LOGISTICS_ROLE_IDS.includes(Number(r?.id)))
    : warehouseRoleTitle(user) !== null;
}

export function canUseDriverDashboard(user: any): boolean {
  return userRoles(user).some(r => DRIVER_ROLE_IDS.includes(Number(r?.id)));
}

export function warehouseRoleTitle(user: any): string | null {
  const titles = userRoles(user).map(r => normalize(r?.name));
  return WAREHOUSE_ROLE_TITLES.find(title => titles.includes(normalize(title))) ?? null;
}

// Users with both kinds of role land on the dashboard they used last (logistics if unknown).
// null = no access to either.
export function homeArea(user: any): DashboardArea | null {
  const logistics = canUseArea(user, 'logistics');
  const warehouse = canUseArea(user, 'warehouse');
  if (logistics && warehouse) return readLastArea(user) ?? 'logistics';
  if (logistics) return 'logistics';
  if (warehouse) return 'warehouse';
  return null;
}

// Remembered per user in a cookie, only for users who can use both.
export function rememberArea(user: any, area: DashboardArea) {
  if (canUseArea(user, 'logistics') && canUseArea(user, 'warehouse')) {
    document.cookie = `${cookieName(user)}=${area}; path=/; max-age=${ONE_YEAR_SEC}; SameSite=Lax`;
  }
}

function readLastArea(user: any): DashboardArea | null {
  const name = cookieName(user);
  const value = document.cookie.split('; ').find(c => c.startsWith(`${name}=`))?.slice(name.length + 1);
  return value === 'logistics' || value === 'warehouse' ? value : null;
}

function cookieName(user: any): string {
  return `dashboard_home_${user?.id}`;
}

function userRoles(user: any): any[] {
  return Array.isArray(user?.roles) ? user.roles : [];
}

function normalize(title: unknown): string {
  return String(title ?? '').trim().toLowerCase();
}
